import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const verifyAuth = useCallback(async () => {
    try {
      await axios.get(`${API}/admin/verify`);
      setIsAuthenticated(true);
    } catch {
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const login = useCallback(async (username, password) => {
    try {
      await axios.post(`${API}/admin/login`, { username, password });
      setIsAuthenticated(true);
      toast.success("Connexion réussie");
      return true;
    } catch {
      toast.error("Identifiants incorrects");
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch { /* cookie already expired or server unreachable */ }
    setIsAuthenticated(false);
    toast.info("Déconnexion réussie");
  }, []);

  const contextValue = useMemo(
    () => ({ isAuthenticated, isLoading, login, logout }),
    [isAuthenticated, isLoading, login, logout]
  );

  return (
    <AdminAuthContext.Provider value={contextValue}>
      {children}
    </AdminAuthContext.Provider>
  );
};
